import React from "react";

const HowToPickGreatTargets = () => {
    return (
        <div className="min-h-screen bg-darkblue text-lightgray px-6 py-16">
            <div className="max-w-4xl mx-auto">
                <h1 className="text-4xl text-orange text-center mb-8">
                    How to Pick Great Superstar Targets
                </h1>

                <div className="space-y-8 text-lg">
                    <section>
                        <p>
                            Your Superstar Target is someone you have directly worked with in a similar role—someone with whom you've built an exceptionally effective working relationship. Selecting the right target ensures CareerMission will recommend candidates whose aptitudes, motivations, personality traits, and professional experiences closely mirror your Superstar Target, improving your likelihood of a successful hire.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl text-orange font-semibold mb-4">Perfect Superstar Targets</h2>
                        <ul className="list-disc ml-6 space-y-2">
                            <li><strong>Direct Experience with You:</strong> They've worked directly under you in the same or very similar role.</li>
                            <li><strong>Technical Excellence and Passion:</strong> They excel and genuinely enjoy the work.</li>
                            <li><strong>Strong Relationship with You:</strong> Positive rapport in a direct reporting relationship.</li>
                            <li><strong>Excellent Team Player:</strong> Enhances the overall team's performance.</li>
                            <li><strong>Career Growth Potential:</strong> They've been promoted or clearly deserve it.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl text-orange font-semibold mb-4">Great Alternatives</h2>
                        <p>
                            If you haven't worked directly with a superstar in the exact role, choose someone from a closely related role with whom you had a strong working relationship. Similar aptitudes, motivations, and personality traits can transfer effectively across related roles.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl text-orange font-semibold mb-4">Less-Good Targets</h2>
                        <ul className="list-disc ml-6 space-y-2">
                            <li><strong>Role Mismatch:</strong> Avoid targets with expertise far removed from your hiring role.</li>
                            <li><strong>Challenging Personalities:</strong> Avoid those whose negative traits outweigh their technical strengths.</li>
                            <li><strong>No Prior Relationship (Referral Only):</strong> Use referrals carefully—only if recommended by someone who knows both you and the candidate professionally.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl text-orange font-semibold mb-4">What If You're Hiring for the First Time?</h2>
                        <p className="mb-4">
                            If you're managing a team and hiring for the first time, you might feel unsure about selecting a target. Here's guidance:
                        </p>
                        <ul className="list-disc ml-6 space-y-2">
                            <li><strong>Internal Promotion:</strong> Choose yourself if you were promoted because you excelled in this role.</li>
                            <li><strong>No Direct Management Experience:</strong> Choose a teammate who thrived in the role and exemplified collaboration and teamwork.</li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl text-orange font-semibold mb-4">Why Picking the Right Target Matters (Especially for Technical Managers)</h2>
                        <p>
                            CareerMission complements your technical evaluation processes. Our assessments leverage validated career aptitude measures (such as those from the Strong Interest Inventory) and personality dimensions from the Big Five framework. Strong CareerMission matches typically share similar resumes, skillsets, and experiences with your Superstar Target, helping you quickly prioritize among technically qualified candidates.
                        </p>
                        <p className="mt-4 italic">
                            Example: Think of your best software engineer—not only a strong coder but a mentor who improved the team's productivity through communication and collaborative problem-solving. Selecting them as your Superstar Target enables CareerMission to recommend candidates likely to replicate their technical excellence and team-enhancing behaviors.
                        </p>
                    </section>
                </div>
            </div>
        </div>
    );
};

export default HowToPickGreatTargets;
